import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack } from "@mui/material";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { useCapabilities } from "../../../hooks/useCapabilities";

import { useGetJeopardyStakeholders } from "../../../shared/hooks";
import { ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";

import { filterCommercialCategory } from "../../../shared/utils/filterCommercialCategory";

import { useAddArtefacts, useEditArtefacts, useFetchArtefactsDetails } from "../mutation";
import { useSearchProjects } from "../../projects/mutations";
import { usePhase } from "../../projectOverview/hooks/usePhase";

export interface ArtefactErrors {
  title?: string;
  description?: string;
  project?: string;
  category?: string;
  sub_category?: string;
  assigned_to?: string;
  required_date?: string;
  checklist_id?: string;
  phase?: string;
}

export interface Artefact {
    title: null | string;
    category: null | string;
    sub_category: null | string;
    description: null | string;
    assigned_to: null | string;
    required_date: null | string;
    compliance: boolean;
    checklist_id: null | number;
    project: AutocompleteOption | null;
    phase: null | string;
}

const initialArtefact: Artefact = {
    title: null,
    category: null,
    sub_category: null,
    description: null,
    assigned_to: null,
    required_date: null,
    compliance: false,
    checklist_id: null,
    project: null,
    phase: null
}

const initialArtefactErrors: ArtefactErrors = {
  title: undefined,
  description: undefined,
  project: undefined,
  category: undefined,
  sub_category: undefined,
  assigned_to: undefined,
  required_date: undefined,
  checklist_id: undefined,
  phase: undefined,
};

export const CreateArtefacts: React.FC<{ projectId: string | undefined, stageGateId: number | null, isEdit: boolean, artefactId: string | null, onClose: () => void, onSave: () => void }> = ({ projectId, stageGateId, isEdit, artefactId, onClose, onSave }) => {

    const searchProjects = useSearchProjects();

    const addArtefacts = useAddArtefacts();
    const editArtefacts = useEditArtefacts();
    const { isCommercialPerson, isAdminPerson } = useCapabilities();

    const { data: artefactDetails, isLoading: isPending, removeCache } = useFetchArtefactsDetails(artefactId!);
    const { getFilterOptions } = useDisplayField([]);

    const [previousProjectId, setPreviousProjectId] = useState<string | number | null>(null);
    const [initializedEditArtefactId, setInitializedEditArtefactId] = useState<string | null>(null);
    const [artefact, setArtefact] = useState<Artefact>(initialArtefact);
    const [artefactsErrors, setArtefactssErrors] = useState<ArtefactErrors>(initialArtefactErrors);

    const effectiveProjectId = artefact.project?.id ?? projectId;
    const { data: phaseDataRaw, isPending: isPhasePending } = usePhase(effectiveProjectId ? String(effectiveProjectId) : undefined);

    const params = useMemo(() => {
        if (!effectiveProjectId) return {};
        return { project_id: effectiveProjectId };
    }, [effectiveProjectId]);

    const { data: stakeholders, isLoading: isLoadingUsers, refetch } = useGetJeopardyStakeholders({ params: params });

    const userOptions = useMemo(() => {
        const options = stakeholders?.data?.flatMap((role) =>
            role?.users?.map((user) => ({
                id: String(user.user_id),
                name: user.user_name,
            }))
        ) || [];

        if (
            artefact.assigned_to &&
            artefactDetails?.user_name &&
            !options.some((user) => user.id === artefact.assigned_to)
        ) {
            options.unshift({
                id: artefact.assigned_to,
                name: artefactDetails.user_name,
            });
        }

        return options;
    }, [stakeholders, artefact.assigned_to, artefactDetails?.user_name]);

    const commercialCategories = filterCommercialCategory({ domain: "ARTEFACT_CATEGORY", filterValue: "Commercial", isInclude: isCommercialPerson, isAdminPerson });

    const categoryOptions = useMemo(() => {
        return commercialCategories.map((value) => ({
            label: value.display_text,
            value: value.key
        }));
    }, [commercialCategories]);

    const subCategoryOptions = useMemo(() => {
        return getFilterOptions('ARTEFACT_SUB_CATEGORY')
            .filter((a: { parent_key: string }) => a.parent_key === artefact.category)
            .map((value: { key: string, display_text: string }) => ({
                label: value.display_text,
                value: value.key
            }));
    }, [getFilterOptions, artefact.category]);

    const validateFields = () => {
        const errors: Partial<ArtefactErrors> = {};

        if (!artefact.title) {
            errors.title = "Title is required";
        }

        if (!artefact.description) {
            errors.description = "Description is required";
        }

        if (!artefact.project) {
            errors.project = "Project is required";
        }

        if (!artefact.phase) {
            errors.phase = "Phase is required";
        }

        if (!artefact.category) {
            errors.category = "Category is required";
        }

        if (!artefact.sub_category) {
            errors.sub_category = "Sub-category is required";
        }

        if(!artefact.assigned_to) {
            errors.assigned_to = "Assigned to is required";
        }

        if(!artefact.required_date) {
            errors.required_date = "Required date is required";
        }

        setArtefactssErrors({
            ...initialArtefactErrors,
            ...errors
        });
        return Object.keys(errors).length === 0;
    }

    const onChange = (key: string, value: string | boolean | AutocompleteOption | AutocompleteOption[] | null) => {
        setArtefact((prev) => ({
            ...prev,
            [key]: value
        }));
        if (artefactsErrors[key as keyof typeof artefactsErrors]) {
            setArtefactssErrors((prev) => ({
                ...prev,
                [key]: null
            }));
        }
    };

    const payload = () => {
        return {
            "title": artefact.title,
            "category": artefact.category,
            "sub_category": artefact.sub_category,
            "description": artefact.description,
            "assigned_to": artefact.assigned_to,
            "compliance": artefact.compliance,
            "required_date": artefact.required_date,
            "checklist_id": artefact.checklist_id,
            "project_id": artefact.project?.id || null,
            "project_tree_id": artefact.phase || null,
            "stage_gate_id": stageGateId,
            ...(isEdit && artefactDetails?.status ? { "status": artefactDetails.status } : {})
        }
    };

    const onSubmit = async () => {
        if (!validateFields()) return;
        if (isEdit && artefactId) {
            await editArtefacts.mutateAsync({ payload: payload(), artefactId: String(artefactId) });
        } else {
            await addArtefacts.mutateAsync({ payload: payload() });
        };
        removeCache();
        onSave();
        onClose();
    };

    const fetchProjectDetails = async () => {
        const params: Record<string, string | undefined> = {
            project_id: projectId
        };
        const response = await searchProjects.mutateAsync({ params });
        const project = response?.data[0];
        if(!project) return;
        setArtefact((prev) => ({
            ...prev,
            project: {
                id: project.id,
                label: project.title
            }
        }));
    };

    // Initialize new artefact with project details
    useEffect(() => {
        if (!isEdit && projectId && !artefact.project?.id) {
            fetchProjectDetails();
        }
    }, [isEdit, projectId]);

    // Initialize edit mode with artefact details
    useEffect(() => {
        if (isEdit && artefactDetails && artefactId && initializedEditArtefactId !== artefactId) {
            const initialProject = (artefactDetails.project_id && artefactDetails.project_title)
                ? {
                    id: artefactDetails.project_id,
                    label: artefactDetails.project_title
                }
                : null;


                const initialPhase = artefactDetails?.project_tree_id ?? null;

                setArtefact({
                    title: artefactDetails.title,
                    category: artefactDetails.category,
                    sub_category: artefactDetails.sub_category,
                    description: artefactDetails.description,
                    assigned_to: artefactDetails.assigned_to ? String(artefactDetails.assigned_to) : null,
                    required_date: artefactDetails.required_date,
                    compliance: artefactDetails.compliance === "1",
                    checklist_id: artefactDetails.checklist_id,
                    project: initialProject,
                    phase: initialPhase ? String(artefactDetails.project_tree_id) : null
                });

            setPreviousProjectId(initialProject?.id ?? null);
            setInitializedEditArtefactId(artefactId);
        }
    }, [isEdit, artefactDetails, artefactId, initializedEditArtefactId]);

    // Clear assigned_to on  project change
    useEffect(() => {
        const selectedProjectId = artefact.project?.id ?? null;
        
        if (
            previousProjectId !== null &&
            selectedProjectId !== null &&
            selectedProjectId !== previousProjectId
        ) {
            setArtefact((prev) => ({
                ...prev,
                assigned_to: null,
                phase: null
            }));
        }

        if (selectedProjectId !== previousProjectId) {
            setPreviousProjectId(selectedProjectId);
        }
    }, [artefact.project?.id, previousProjectId]);

    // stakeholder refetch when the project changes
    useEffect(() => {
        const effectiveProjectId = artefact.project?.id || projectId;
        if (effectiveProjectId) {
            refetch();
        }
    }, [artefact.project?.id, projectId]);

    if (isEdit && isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        );
    };

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} height={'100%'}>

            <Box display={'flex'} flexDirection={'column'} gap={2} flex={1} p={2}>

                <FieldControl
                    keyName={'title'}
                    label={"Artefact Title"}
                    variant={"input"}
                    value={artefact.title}
                    onChange={onChange}
                    required
                    error={!!artefactsErrors.title}
                    helperText={artefactsErrors.title}
                />

                <FieldControl
                    keyName={'description'}
                    label={"Description"}
                    variant={"input"}
                    value={artefact.description}
                    onChange={onChange}
                    required
                    error={!!artefactsErrors.description}
                    helperText={artefactsErrors.description}
                />

                <ControlledAutocomplete
                    type="PROJECT"
                    value={artefact.project}
                    onSelect={(values) => onChange("project", values)}
                    label="Search project"
                    placeholder="Search project"
                    required
                    error={!!artefactsErrors.project}
                    helperText={artefactsErrors.project}
                    disabled={!isEdit && !!projectId}
                />
                {
                    artefact.project?.id &&
                    <FieldControl
                        keyName={'phase'}
                        label={"Select Project Phase"}
                        variant={"select"}
                        value={artefact.phase}
                        options={
                            phaseDataRaw?.data?.map((value: { id: string | number; name: string }) => {
                                return {
                                    value: String(value.id),
                                    label: value.name
                                };
                            })
                        }
                        onChange={onChange}
                        loading={isPhasePending}
                        required
                        error={!!artefactsErrors.phase}
                        helperText={artefactsErrors.phase}
                    />
                }



                <FieldControl
                    keyName={'category'}
                    label={"Artefact Category"}
                    value={artefact.category}
                    variant={"select"}
                    options={categoryOptions}
                    onChange={onChange}
                    required
                    error={!!artefactsErrors.category}
                    helperText={artefactsErrors.category}
                />

                <FieldControl
                    keyName={'sub_category'}
                    label={"Artefact Sub-Category"}
                    value={artefact.sub_category}
                    variant={"select"}
                    options={subCategoryOptions}
                    onChange={onChange}
                    required
                    error={!!artefactsErrors.sub_category}
                    helperText={artefactsErrors.sub_category}
                />

                <FieldControl
                    keyName={'assigned_to'}
                    label={"Assigned to"}
                    variant={"select"}
                    options={userOptions.map((u) => ({
                        value: u.id,
                        label: u.name,
                    }))}
                    loading={isLoadingUsers}
                    value={artefact.assigned_to}
                    onChange={onChange}
                    required
                    error={!!artefactsErrors.assigned_to}
                    helperText={artefactsErrors.assigned_to}
                />

                <FieldControl
                    keyName={'required_date'}
                    label={"Required Date"}
                    variant={"datetime"}
                    value={artefact.required_date}
                    onChange={onChange}
                    minDate={new Date().toISOString().split('T')[0]}
                    required
                    error={!!artefactsErrors.required_date}
                    helperText={artefactsErrors.required_date}
                />

            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button variant="outlined" onClick={onClose} loading={addArtefacts.isPending || editArtefacts.isPending}>Close</Button>
                <Button variant="contained" color="primary" onClick={onSubmit} loading={addArtefacts.isPending || editArtefacts.isPending}>{isEdit ? 'Save Changes' : 'Create Artefact'}</Button>
            </Stack>

        </Box>
    );
};